import React, { useCallback, useEffect, useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Stack,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListItemText,
  OutlinedInput,
  Checkbox,
  Button,
  Tooltip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { Box, styled } from "@mui/system";
import { LoadingButton } from "@mui/lab";
import { Close } from "@mui/icons-material";
import { errorColor } from "../../sdk";

const MAX_DAYS_CHECK = 90;
const EMAIL_TRUNCATE_LENGTH = 25;
const DOW_TRUNCATE_LENGTH = 13;

const convertArrayToString = (arr) => {
  if (Array.isArray(arr)) {
    return arr.join(", ");
  }
  return "";
};

const convertArrayOfWeekToString = (arr, week) => {
  if (Array.isArray(arr)) {
    return arr.map((day) => week[day]).join(", ");
  }
  return "";
};

// Custom hook for debounced email input
const useDebouncedEmail = (initialValue, onUpdate, delay = 500) => {
  const [localValue, setLocalValue] = useState(initialValue);

  useEffect(() => {
    setLocalValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (localValue !== initialValue) {
        onUpdate(localValue);
      }
    }, delay);

    return () => clearTimeout(timer);
  }, [localValue, onUpdate, delay, initialValue]);

  return [localValue, setLocalValue];
};

const RateShopAccordion = ({
  item,
  index,
  guests,
  nights,
  rateshopType,
  allSources,
  handleChange,
  handleSaveRateShop,
  isRateShopLoading,
  isLoading,
  setSchedulingName,
  formEdit,
  setformEdit,
  week,
  handleSaveScheduling,
  scheduleError,
  schedulingName,
  handleDeleteScheduling,
  handleDeleteNewSchedule,
  handleDeleteRateShop,
  handleAddScheduling,
  platforms,
  editSchedulingEnable,
  setEditSchedulingEnable,
  handleUpdateScheduling,
  setScheduleError,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [editMode, setEditMode] = useState(false);

  const sourcesById = React.useMemo(() => {
    return new Map(allSources.map((s) => [s.id, s]));
  }, [allSources]);

  const handleChangeAccordion = () => {
    if (!editMode) {
      setExpanded(!expanded);
    }
  };

  const handleClickEditButton = () => {
    setExpanded(false);
    setEditMode(!editMode);
  };

  const memoizedSchedulingName = React.useMemo(() => {
    return (rateShopId) =>
      schedulingName?.filter((i) => i?.rateShopId === rateShopId);
  }, [schedulingName]);

  const scheduleCount = memoizedSchedulingName(item?.rateShopId)?.length || 0;
  const fullSources =
    item?.sources
      ?.map((src) => `${src?.name} ${src?.isMobile ? "(Mobile)" : "(Desktop)"}`)
      ?.join(", ") || "";

  const MAX_LEN = 25;
  const shortSources =
    fullSources.length > MAX_LEN
      ? fullSources.substring(0, MAX_LEN) + "..."
      : fullSources;

  const selectedSourceIds = React.useMemo(
    () => new Set(item?.sources?.map((s) => s.id)),
    [item?.sources]
  );

  return (
    <Accordion expanded={expanded} onChange={handleChangeAccordion}>
      <AccordionSummary expandIcon={null}>
        <RateShopWrapperStack>
          {editMode ? (
            <Stack
              direction="row"
              alignItems="center"
              width="100%"
              gap={2}
              sx={{ flexWrap: "nowrap" }}
            >
              <TextField
                label="Name"
                value={item?.name}
                onChange={(e) => handleChange(e, "name", index)}
                error={!item?.name}
                sx={{ flex: "0 0 12%", minWidth: 0 }}
              />

              <FormControl sx={{ flex: "0 0 10%", minWidth: 0 }}>
                <InputLabel id="type-label" error={item?.hotelOnly == null}>
                  Type
                </InputLabel>
                <Select
                  labelId="type-label"
                  value={item?.hotelOnly ? "Rate Parity" : "Rateshop"}
                  onChange={(e) => handleChange(e, "type", index)}
                  input={<OutlinedInput label="Type" />}
                  error={item?.hotelOnly == null}
                >
                  {rateshopType.map((type) => (
                    <MenuItem key={type} value={type}>
                      <ListItemText primary={type} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl sx={{ flex: "0 0 10%", minWidth: 0 }}>
                <InputLabel id="guest-label" error={!item?.occupancy}>
                  Guest
                </InputLabel>
                <Select
                  labelId="guest-label"
                  value={item?.occupancy}
                  onChange={(e) => handleChange(e, "occupancy", index)}
                  input={<OutlinedInput label="Guest" />}
                  error={!item?.occupancy}
                >
                  {guests.map((guest) => (
                    <MenuItem key={guest} value={guest}>
                      <ListItemText primary={guest} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl sx={{ flex: "0 0 10%", minWidth: 0 }}>
                <InputLabel id="los-label" error={!item?.los}>
                  LOS
                </InputLabel>
                <Select
                  labelId="los-label"
                  value={item?.los}
                  onChange={(e) => handleChange(e, "los", index)}
                  input={<OutlinedInput label="LOS" />}
                  error={!item?.los}
                >
                  {nights.map((night) => (
                    <MenuItem key={night} value={night}>
                      <ListItemText primary={night} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {item?.hotelOnly && (
                <FormControl sx={{ flex: "0 0 10%", minWidth: 0 }}>
                  <InputLabel id="type-platform" error={!item?.platform}>
                    Platform Type
                  </InputLabel>
                  <Select
                    labelId="type-platform"
                    value={item?.platform}
                    onChange={(e) => handleChange(e, "platform", index)}
                    input={<OutlinedInput label="Platform Type" />}
                    error={!item?.platform}
                  >
                    {platforms.map((type) => (
                      <MenuItem key={type} value={type}>
                        <ListItemText primary={type} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              <TextField
                label="No. of Days"
                type="number"
                value={item?.days}
                onChange={(e) => handleChange(e, "days", index)}
                error={!item?.days || item?.days > MAX_DAYS_CHECK}
                helperText={
                  item?.days > MAX_DAYS_CHECK
                    ? `Max ${MAX_DAYS_CHECK} days allowed`
                    : ""
                }
                sx={{ flex: "0 0 12%", minWidth: 0 }}
              />

              <FormControl sx={{ flex: "0 0 20%", minWidth: 0 }}>
                <InputLabel id="ota-label" error={!item?.sources?.length}>
                  OTA
                </InputLabel>
                <Select
                  labelId="ota-label"
                  multiple={item?.hotelOnly}
                  value={
                    item?.hotelOnly
                      ? item?.sources?.map((s) => s.id)
                      : item?.sources?.[0]?.id || ""
                  }
                  onChange={(e) => handleChange(e, "sources", index)}
                  input={<OutlinedInput label="OTA" />}
                  error={!item?.sources?.length}
                  renderValue={(selected) => {
                    if (item?.hotelOnly) {
                      const names = selected
                        .map((id) => {
                          const src = sourcesById.get(id);
                          if (!src) return null;
                          return src.isMobile
                            ? `${src.name} (Mobile)`
                            : `${src.name} (Desktop)`;
                        })
                        .filter(Boolean);

                      return names.join(", ").substring(0, 35);
                    } else {
                      const src = sourcesById.get(selected);
                      if (!src) return "";
                      return src.isMobile
                        ? `${src.name} (Mobile)`
                        : `${src.name} (Desktop)`;
                    }
                  }}
                >
                  {allSources
                    .filter((src) => {
                      if (!item?.hotelOnly) {
                        return !src.isBrandWebsite;
                      }

                      // For hotelOnly, brand websites are always included.
                      if (src.isBrandWebsite) {
                        return true;
                      }

                      // For hotelOnly, filter other sources based on platform.
                      if (item?.platform === "Desktop") {
                        return !src.isMobile;
                      }
                      if (item?.platform === "Mobile") {
                        return src.isMobile;
                      }

                      // If no platform is selected, include all other sources.
                      return true;
                    })
                    .map((src) => (
                      <MenuItem key={src.id} value={src.id}>
                        {item?.hotelOnly && (
                          <Checkbox checked={selectedSourceIds.has(src.id)} />
                        )}

                        <ListItemText
                          primary={
                            src.isMobile
                              ? `${src.name} (Mobile)`
                              : `${src.name} (Desktop)`
                          }
                        />
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>

              <LoadingButton
                variant="contained"
                onClick={() => handleSaveRateShop(index)}
                loading={isRateShopLoading?.includes(index)}
                sx={{ flex: "0 0 8%", minWidth: 0 }}
              >
                Save
              </LoadingButton>

              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  handleClickEditButton();
                }}
                sx={{ flex: "0 0 4%" }}
              >
                <Close />
              </IconButton>

              <IconButton
                sx={{ color: "#1976D2", flex: "0 0 4%" }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteRateShop(item?.rateShopId);
                }}
              >
                <DeleteIcon />
              </IconButton>
            </Stack>
          ) : (
            <>
              <RateShopNameRowStack>
                <Stack direction={"row"} alignItems="center" gap={1}>
                  <ExpandMoreIcon
                    sx={{
                      transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                      transition: "transform 0.3s",
                    }}
                  />
                  <Typography sx={{ fontWeight: 600 }}>{item?.name}</Typography>
                  <StyledChip>{`${scheduleCount} Schedules`}</StyledChip>
                </Stack>

                <IconStack>
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      handleClickEditButton(e);
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    sx={{
                      color: "#1976D2",
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteRateShop(item?.rateShopId);
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </IconStack>
              </RateShopNameRowStack>
              <RateShopDetailsRowStack>
                <Typography>
                  <strong>Guest:</strong> {item?.occupancy}
                </Typography>
                <Typography>
                  <strong>Type:</strong>{" "}
                  {item?.hotelOnly ? "Rate Parity" : "Rateshop"}
                </Typography>

                <Typography>
                  <strong>LOS:</strong> {item?.los}
                </Typography>

                <Typography>
                  <strong>No. of days:</strong> {item?.days}
                </Typography>

                <Typography>
                  <strong>OTA: </strong>
                  <Tooltip title={fullSources}>
                    <span style={{ cursor: "pointer" }}>{shortSources}</span>
                  </Tooltip>
                </Typography>
              </RateShopDetailsRowStack>
            </>
          )}
        </RateShopWrapperStack>
      </AccordionSummary>

      <AccordionDetails>
        <Stack paddingX={2} gap={2}>
          <Stack>
            <Typography fontWeight={600}>Schedules</Typography>
          </Stack>
          <SchedulingRows
            schedulingName={memoizedSchedulingName(item?.rateShopId)}
            allSchedulingName={schedulingName}
            handleDeleteScheduling={handleDeleteScheduling}
            handleDeleteNewSchedule={handleDeleteNewSchedule}
            scheduleError={scheduleError}
            handleSaveScheduling={handleSaveScheduling}
            week={week}
            formEdit={formEdit}
            setformEdit={setformEdit}
            setSchedulingName={setSchedulingName}
            isLoading={isLoading}
            editSchedulingEnable={editSchedulingEnable}
            setEditSchedulingEnable={setEditSchedulingEnable}
            handleUpdateScheduling={handleUpdateScheduling}
            setScheduleError={setScheduleError}
          />
          <Stack direction={"row"} width={"100%"} justifyContent={"flex-end"}>
            <Button
              variant="contained"
              onClick={() => handleAddScheduling(item?.rateShopId)}
            >
              + Add Scheduling
            </Button>
          </Stack>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

export default RateShopAccordion;

// Debounced Email Field Component
const DebouncedEmailField = ({
  value,
  onChange,
  error,
  globalIndex,
  setformEdit,
  setScheduleError
}) => {
  const handleEmailUpdate = useCallback(
    (newValue) => {
      setformEdit(true);
      setScheduleError(false);
      const emailArray = newValue.split(",").map((email) => email.trim());
      onChange(emailArray, globalIndex);
    },
    [onChange, globalIndex, setformEdit, setScheduleError]
  );

  const [localEmail, setLocalEmail] = useDebouncedEmail(
    value?.join(",") || "",
    handleEmailUpdate,
    800 // 800ms delay
  );

  return (
    <TextField
      sx={{ width: "32%" }}
      label="Email"
      type="email"
      value={localEmail}
      onChange={(e) => setLocalEmail(e.target.value)}
      error={error}
    />
  );
};

const SchedulingRows = ({
  schedulingName,
  handleDeleteScheduling,
  handleDeleteNewSchedule,
  scheduleError,
  handleSaveScheduling,
  week,
  formEdit,
  setformEdit,
  setSchedulingName,
  isLoading,
  allSchedulingName, // Add this to get access to the full array
  editSchedulingEnable,
  setEditSchedulingEnable,
  handleUpdateScheduling, 
  setScheduleError
}) => {
  const handleSchedulingChange = (e, changeField, globalIndex) => {
    setformEdit(true);
    setScheduleError(false);
    let value = e.target.value;
    switch (changeField) {
      case "rateshop":
        value = e.target.value.name;
        setSchedulingName((prev) => {
          const updatedSchedulingName = [...prev];
          updatedSchedulingName[globalIndex].rateshop = value;
          updatedSchedulingName[globalIndex].rateShopId =
            e.target.value.rateShopId;
          return updatedSchedulingName;
        });
        break;
      case "hour":
        setSchedulingName((prev) => {
          const updatedSchedulingName = [...prev];
          updatedSchedulingName[globalIndex].hour = value;
          return updatedSchedulingName;
        });
        break;
      case "minute":
        setSchedulingName((prev) => {
          const updatedSchedulingName = [...prev];
          updatedSchedulingName[globalIndex].minute = value;
          return updatedSchedulingName;
        });
        break;
      case "dow":
        setSchedulingName((prev) => {
          const updatedSchedulingName = [...prev];
          updatedSchedulingName[globalIndex].dow = value;
          return updatedSchedulingName;
        });

        break;
      default:
        break;
    }
  };

  // Separate handler for email updates from the debounced component
  const handleEmailUpdate = useCallback(
    (emailArray, globalIndex) => {
      setSchedulingName((prev) => {
        const updatedSchedulingName = [...prev];
        updatedSchedulingName[globalIndex].emails = emailArray;
        return updatedSchedulingName;
      });
    },
    [setSchedulingName]
  );

  const handleEditSchedule = (scheduledId) => {
    setEditSchedulingEnable((prev) => [...prev, scheduledId]);
  };


  const handleSaveButton = (globalIndex, item) => {
    if (item?.scheduledId) {
      handleUpdateScheduling(globalIndex, item);
    } else {
      handleSaveScheduling(globalIndex);
    }
  }

  return schedulingName?.map((item) => {
    const emailStr = convertArrayToString(item?.emails);
    const dowStr = convertArrayOfWeekToString(item?.dow, week);
    // Find the global index of this item in the full array
    const globalIndex = allSchedulingName?.findIndex(
      (globalItem) => globalItem === item
    );
    return (
      <SchedulingRowsStack>
        <Stack width="100%" gap="24px">
          <Stack direction="row" gap="24px" alignItems={"center"}>
            <Stack direction="row" gap={14} width="100%" alignItems={"center"}>
              {!item?.scheduledId || editSchedulingEnable.includes(item?.scheduledId) ?   (
                <Stack width="100%">
                  <Stack
                    direction={"row"}
                    width={"100%"}
                    justifyContent={"space-between"}
                    alignItems={"center"}
                  >
                    <DebouncedEmailField
                      value={item?.emails}
                      onChange={handleEmailUpdate}
                      error={scheduleError && !(item?.emails?.length > 0)}
                      globalIndex={globalIndex}
                      setformEdit={setformEdit}
                      setScheduleError={setScheduleError}
                    />
                    <FormControl sx={{ width: "20%" }}>
                      <InputLabel
                        id={`label-dow-${globalIndex}`}
                        error={scheduleError && !item?.dow}
                      >
                        DOW
                      </InputLabel>
                      <Select
                        labelId={`label-dow-${globalIndex}`}
                        multiple
                        value={item?.dow ?? []}
                        onChange={(e) =>
                          handleSchedulingChange(e, "dow", globalIndex)
                        }
                        input={<OutlinedInput label="DOW" />}
                        renderValue={(selectedDays) => {
                          const daystoDisplay = selectedDays.map((day) =>
                            week.find((_, index) => index === day)
                          );
                          return daystoDisplay.join(", ");
                        }}
                        MenuProps={{
                          PaperProps: {
                            style: {
                              maxHeight: 300,
                            },
                          },
                        }}
                      >
                        {week.map((day, index) => (
                          <MenuItem key={index} value={index}>
                            <Checkbox checked={item?.dow?.includes(index)} />
                            <ListItemText primary={day} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl sx={{ width: "10%" }}>
                      <InputLabel
                        id={`label-hour-${globalIndex}`}
                        error={scheduleError && !item?.hour}
                      >
                        Hour
                      </InputLabel>
                      <Select
                        labelId={`label-hour-${globalIndex}`}
                        id="demo-select"
                        value={item?.hour}
                        onChange={(e) =>
                          handleSchedulingChange(e, "hour", globalIndex)
                        }
                        error={scheduleError && !item?.hour}
                        input={<OutlinedInput label="Hour" />}
                        MenuProps={{
                          PaperProps: {
                            style: {
                              maxHeight: 300,
                            },
                          },
                        }}
                      >
                        {[...Array(24)].map((_, index) => {
                          const hour = index;
                          const time = `${hour < 10 ? "0" : ""}${hour}:00`;
                          return (
                            <MenuItem key={time} value={time}>
                              {time}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                    <FormControl sx={{ width: "10%" }}>
                      <InputLabel
                        id={`label-minute-${globalIndex}`}
                        error={scheduleError && !item?.minute}
                      >
                        Minutes
                      </InputLabel>
                      <Select
                        labelId={`label-minute-${globalIndex}`}
                        value={item?.minute}
                        onChange={(e) =>
                          handleSchedulingChange(e, "minute", globalIndex)
                        }
                        error={scheduleError && !item?.minute}
                        input={<OutlinedInput label="Minutes" />}
                        MenuProps={{
                          PaperProps: {
                            style: {
                              maxHeight: 300,
                            },
                          },
                        }}
                      >
                        {[...Array(60)].map((_, index) => {
                          const minute = index;
                          const time = `00:${minute < 10 ? "0" : ""}${minute}`;
                          return (
                            <MenuItem key={time} value={time}>
                              {time}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                    <LoadingButton
                      variant="contained"
                      sx={{ height: "40px", width: "90px" }}
                      onClick={() => handleSaveButton(globalIndex, item)}
                      loading={isLoading?.includes(globalIndex)}
                      disabled={
                        scheduleError ||
                        !formEdit ||
                        isLoading?.includes(globalIndex)
                      } 
                    >
                      Save
                    </LoadingButton>
                    <IconButton
                      onClick={() => handleDeleteNewSchedule(globalIndex, item)}
                    >
                      <Close />
                    </IconButton>
                  </Stack>
                  <Stack
                    width={"100%"}
                    justifyContent={"center"}
                    paddingTop={2}
                  >
                    <Typography
                      variant="caption"
                      textAlign={"center"}
                      color={errorColor}
                    >
                      Rate-shop schedules are typically completed within 2 to 3
                      hours.
                    </Typography>
                  </Stack>
                </Stack>
              ) :  (
                <>
                  <Typography sx={{ width: "25%" }}>
                    <strong>Emails:</strong>{" "}
                    <Tooltip title={emailStr}>
                      {emailStr?.length < EMAIL_TRUNCATE_LENGTH
                        ? emailStr
                        : emailStr.slice(0, EMAIL_TRUNCATE_LENGTH) + "..."}
                    </Tooltip>
                  </Typography>
                  <Typography sx={{ width: "15%" }}>
                    <strong>DOW:</strong>{" "}
                    <Tooltip title={dowStr}>
                      {dowStr?.length < DOW_TRUNCATE_LENGTH
                        ? dowStr
                        : dowStr.slice(0, DOW_TRUNCATE_LENGTH) + "..."}
                    </Tooltip>
                  </Typography>
                  <Typography>
                    <strong>Schedule Time:</strong> {item?.scheduleTime}
                  </Typography>
                  <Typography>
                    <strong>Expected Complete Time:</strong>{" "}
                    {item?.expectedCompleteTime}
                  </Typography>
                </>
              ) }
            </Stack>
              <IconStack>
                {item?.scheduledId && !(editSchedulingEnable).includes(item?.scheduledId) && (
                  <Tooltip title="Edit Schedule">
                    <IconButton onClick={() => handleEditSchedule(item?.scheduledId)}>
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </IconStack>  
            <IconStack>
              {item.scheduledId && !(editSchedulingEnable).includes(item?.scheduledId) && (
                <IconButton
                  sx={{
                    color: "#1976D2",
                  }}
                  onClick={() => handleDeleteScheduling(item?.scheduledId)}
                >
                  <DeleteIcon />
                </IconButton>
              )}
            </IconStack>
          </Stack>
        </Stack>
      </SchedulingRowsStack>
    );
  });
};

// Styled Components
const RateShopWrapperStack = styled(Stack)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  width: "100%",
  padding: theme.spacing(1),
}));

const RateShopNameRowStack = styled(Stack)(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  flexDirection: "row",
  width: "100%",
}));

const IconStack = styled(Stack)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: theme.spacing(1),
}));

const RateShopDetailsRowStack = styled(Stack)(({ theme, editMode }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: theme.spacing(14),
}));

const SchedulingRowsStack = styled(Stack)(({ theme }) => ({
  backgroundColor: "#F8FAFC4D",
  border: "1px solid #E2E8F0",
  borderRadius: "6px",
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  alignItems: "center",
  position: "relative",
  padding: theme.spacing(2),
}));

const StyledChip = styled(Box)(() => ({
  backgroundColor: "#6B7280",
  color: "#FFFFFF",
  fontSize: "14px",
  borderRadius: "4px",
  padding: "3px 6px",
  fontWeight: 500,
}));
